import React, { useState, useEffect, useRef } from 'react';
import { FaVolumeUp, FaVolumeMute, FaSun, FaWater } from 'react-icons/fa';

const Header = () => {
  const [remainingTime, setRemainingTime] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  // Fixed target end date: April 20, 2025
  const endDate = new Date('April 20, 2025 23:59:59').getTime();

  const calculateRemainingTime = () => {
    const timeRemaining = endDate - Date.now();
    return timeRemaining > 0 ? timeRemaining : 0;
  };

  const formatTime = (time) => {
    const days = Math.floor(time / (1000 * 60 * 60 * 24));
    const hours = Math.floor((time % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((time % (1000 * 60)) / 1000);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      const remaining = calculateRemainingTime();
      setRemainingTime(remaining);

      if (remaining <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuteState = !isMuted;
      setIsMuted(newMuteState);
      videoRef.current.muted = newMuteState;
    }
  };

  return (
    <div className="header-container">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .header-container {
          position: relative;
          height: 100vh;
          width: 100vw;
          overflow: hidden;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .video-container {
          position: relative;
          height: 100vh;
          width: 100vw;
          overflow: hidden;
          cursor: pointer;
        }

        .background-video {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          min-width: 100%;
          min-height: 100%;
          object-fit: cover;
          z-index: 1;
          filter: brightness(0.8) contrast(1.1) saturate(1.2);
        }

        .video-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            rgba(102, 126, 234, 0.15) 0%,
            rgba(118, 75, 162, 0.15) 50%,
            rgba(255, 107, 107, 0.1) 100%
          );
          z-index: 2;
        }

        .sound-control {
          position: absolute;
          top: 24px;
          right: 24px;
          z-index: 10;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .sound-control:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }

        .sound-control:active {
          transform: scale(0.95);
        }

        .floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 3;
          pointer-events: none;
        }

        .floating-icon {
          position: absolute;
          color: rgba(255, 255, 255, 0.1);
          animation: float 6s ease-in-out infinite;
        }

        .floating-icon:nth-child(1) {
          top: 20%;
          left: 10%;
          animation-delay: 0s;
        }

        .floating-icon:nth-child(2) {
          top: 60%;
          right: 15%;
          animation-delay: 2s;
        }

        .floating-icon:nth-child(3) {
          bottom: 30%;
          left: 15%;
          animation-delay: 4s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.1; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 0.3; }
        }

        .content-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          color: white;
          z-index: 5;
          max-width: 90%;
          width: 100%;
          padding: 0 20px;
        }

        .content-card {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 24px;
          padding: 48px 40px;
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .content-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        .main-title {
          font-size: clamp(1.8rem, 5vw, 3.5rem);
          font-weight: 800;
          margin-bottom: 24px;
          background: linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        .description {
          font-size: clamp(1rem, 2.5vw, 1.25rem);
          line-height: 1.7;
          margin-bottom: 32px;
          color: rgba(255, 255, 255, 0.95);
          font-weight: 400;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }

        .cta-section {
          margin-top: 32px;
        }

        .website-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%);
          color: #333;
          text-decoration: none;
          padding: 16px 32px;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 25px rgba(255, 107, 107, 0.3);
          position: relative;
          overflow: hidden;
        }

        .website-link::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          transition: left 0.5s;
        }

        .website-link:hover::before {
          left: 100%;
        }

        .website-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(255, 107, 107, 0.4);
        }

        .website-link:active {
          transform: translateY(0);
        }

        .timer-section {
          position: absolute;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 5;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 16px 24px;
          text-align: center;
          min-width: 280px;
        }

        .timer-label {
          color: #ffd93d;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .timer-display {
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
          font-family: 'Courier New', monospace;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .content-card {
            padding: 32px 24px;
            border-radius: 20px;
          }

          .sound-control {
            top: 16px;
            right: 16px;
            width: 48px;
            height: 48px;
          }

          .timer-section {
            bottom: 20px;
            min-width: 250px;
            padding: 12px 20px;
          }

          .timer-display {
            font-size: 1.2rem;
          }
        }

        @media (max-width: 480px) {
          .content-card {
            padding: 24px 20px;
            border-radius: 16px;
          }

          .description {
            margin-bottom: 24px;
          }

          .website-link {
            padding: 12px 24px;
            font-size: 1rem;
          }

          .floating-elements {
            display: none;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className="video-container" onClick={() => videoRef.current?.play()}>
        <video
          ref={videoRef}
          className="background-video"
          src="https://machaxidev.blob.core.windows.net/frontend-machaxi/BatmintonSummerCamp.mp4"
          autoPlay
          loop
          playsInline
          muted={isMuted}
          controls={false}
        >
          Your browser does not support the video tag.
        </video>
        
        <div className="video-overlay" />
      </div>

      <div className="sound-control" onClick={toggleMute}>
        {isMuted ? <FaVolumeMute size={24} color="white" /> : <FaVolumeUp size={24} color="white" />}
      </div>

      <div className="floating-elements">
        <FaSun className="floating-icon" size={60} />
        <FaWater className="floating-icon" size={50} />
        <FaSun className="floating-icon" size={40} />
      </div>

      <div className="content-overlay">
        <div className="content-card">
          <h1 className="main-title">Welcome to Our Summer Camp Website!</h1>
          <p className="description">
          Our exciting summer camp adventures are taking a short break to gear up for even more fun next year! Stay tuned for updates and get ready to embark on new unforgettable experiences.
            <br /><br />
            See you soon under the summer sun!
          </p>
          <div className="cta-section">
            <a 
              href="https://machaxi.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="website-link"
            >
              <span>Visit machaxi.com</span>
              <span>→</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;